import { getInitials } from "@mirage/shared/account";
import { AccessType } from "@mirage/shared/compose/assist-api";
import {
  isPreConfiguredTemplateID,
  type AssistCustomTemplate,
  type AssistTemplate,
} from "@mirage/shared/compose/assist-template";
import {
  isPreConfiguredVoiceID,
  type ComposeVoice,
} from "@mirage/shared/compose/compose-voice";

export interface ItemDetailsResult {
  avatarLetter: string;
  labelKey:
    | "ai_tool_library_by_dash"
    | "ai_tool_library_from_your_company"
    | "ai_tool_library_by_you";
  creatorI18nKey:
    | "ai_tool_library_created_by_dash"
    | "ai_tool_library_created_by_team_member"
    | "ai_tool_library_created_by_you";
  isIndividual: boolean;
  getAvatarAlt: (displayName?: string) => string;
  getProfilePhotoUrl: (accountPhotoUrl?: string) => string | undefined;
}

type ProcessableItem = ComposeVoice | AssistTemplate;

function isPreConfiguredItem(item: ProcessableItem): boolean {
  return isPreConfiguredVoiceID(item.id) || isPreConfiguredTemplateID(item.id);
}

function isOwnedByUser(item: ProcessableItem): boolean {
  if ("is_owned" in item) {
    return item.is_owned === true;
  }
  if ("isOwned" in item) {
    return (item as AssistCustomTemplate).isOwned === true;
  }
  return false;
}

function getAccessType(item: ProcessableItem): AccessType | undefined {
  return "accessType" in item ? item.accessType : undefined;
}

/**
 * Consolidates the logic for determining avatar letters, creator labels, and individual status
 * for both tones (ComposeVoice) and templates (AssistTemplate).
 *
 * @param item - Either a ComposeVoice (tone) or AssistTemplate
 * @returns Object containing avatar letter, label keys, and isIndividual flag
 */
export function getItemDetails(item: ProcessableItem): ItemDetailsResult {
  // Check if it's a pre-configured item (Dash-created)
  if (isPreConfiguredItem(item)) {
    return {
      avatarLetter: "D",
      labelKey: "ai_tool_library_by_dash",
      creatorI18nKey: "ai_tool_library_created_by_dash",
      isIndividual: false,
      getAvatarAlt: () => "D",
      getProfilePhotoUrl: () => undefined,
    };
  }

  // Check if the item is owned by the user
  if (isOwnedByUser(item)) {
    return {
      avatarLetter: "Y",
      labelKey: "ai_tool_library_by_you",
      creatorI18nKey: "ai_tool_library_created_by_you",
      isIndividual: true,
      getAvatarAlt: (displayName?: string) => getInitials(displayName ?? ""),
      getProfilePhotoUrl: (accountPhotoUrl?: string) => accountPhotoUrl,
    };
  }

  // If not owned and has TEAM access type, it's from company
  const accessType = getAccessType(item);
  if (accessType === AccessType.TEAM) {
    return {
      avatarLetter: "C",
      labelKey: "ai_tool_library_from_your_company",
      creatorI18nKey: "ai_tool_library_created_by_team_member",
      isIndividual: false,
      getAvatarAlt: () => "C",
      getProfilePhotoUrl: () => undefined,
    };
  }

  // Default to individual
  return {
    avatarLetter: "Y",
    labelKey: "ai_tool_library_by_you",
    creatorI18nKey: "ai_tool_library_created_by_you",
    isIndividual: true,
    getAvatarAlt: (displayName?: string) => getInitials(displayName ?? ""),
    getProfilePhotoUrl: (accountPhotoUrl?: string) => accountPhotoUrl,
  };
}
